import { createAction } from '@reduxjs/toolkit';
import type { AppDispatch, RootState } from '@/app/store';
import type { AnyAppData, AppDataRequestKey } from '@/server/app-data';
import { api } from '@/lib/trpc/client';
import { setUser } from './user';

function getTrcpErrorMessage(error: any) {
  return error?.message || error?.data?.code;
}

export const fetchProductPageDataStarted = createAction('product/product-page-data-fetch/started');
export const fetchProductPageDataFailed = createAction<string>(
  'product/product-page-data-fetch/failed'
);
export const fetchProductPageDataSucceeded = createAction<AnyAppData>(
  'product/product-page-data-fetch/succeeded'
);

/**
 *  Fetches product page data from API
 *
 * @param {*} properties - properties to fetch
 * @param {*} [forceUpdate] - if true, fetches data even if it already exists in state
 * @returns {Function} - thunk
 */
export const fetchProductPageData =
  (properties: AppDataRequestKey[], forceUpdate?: boolean) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const userState = state.user;
    const { user, activeChain } = userState;

    const productPageDataState = state.productPageData;
    const { productPageData, isLoading } = productPageDataState;

    if (isLoading) {
      return;
    }

    // Filter out properties that already exist in state
    const propertiesToFetch = forceUpdate
      ? properties
      : properties.filter((property) => !productPageData[property]);

    if (!propertiesToFetch.length) {
      return;
    }

    dispatch(fetchProductPageDataStarted());
    return api.common.getAppData
      .query({
        properties: propertiesToFetch,
        chain: activeChain,
      })
      .then((data) => {
        dispatch(fetchProductPageDataSucceeded(data));

        if (data.chainHiddenFields) {
          const chainHiddenFields = data.chainHiddenFields || [];
          const _chainHiddenFields = chainHiddenFields.filter(
            (field) => !user.hiddenFields.includes(field)
          );
          const _userHiddenFields = [...user.hiddenFields, ..._chainHiddenFields];
          dispatch(setUser({ user: { ...user, hiddenFields: _userHiddenFields } }));
        }
      })
      .catch((error) => {
        dispatch(fetchProductPageDataFailed(getTrcpErrorMessage(error)));
      });
  };
